import { useEffect, useState } from "react";

import {
  onConsentChange,
  setConsent,
  shouldAskForConsent,
  track,
} from "@/lib/analytics";

// THE COOKIE BAR. One question, once, at the bottom of the page.
//
// What it decides is described in full in src/lib/analytics.ts: Decline (and
// "not answered yet") runs the site on sessionStorage with no replay, no
// heatmaps and nothing loaded from PostHog's host; Accept adds the ordinary
// cookie, session replay (every input and ALL text masked, the waitlist form
// blocked outright) and heatmaps.
//
// FIVE DELIBERATE CHOICES:
//
//  1. NON-BLOCKING. No overlay, no scrim, no scroll lock. The site is readable
//     with the bar on screen, and a visitor who ignores it forever is measured
//     on the cookieless tier forever. A modal that holds the page hostage is
//     the pattern the research (docs/design/research/landing-ux-research.md) counts as a
//     conversion cost, and there is nothing here worth paying it for.
//  2. AFTER MOUNT ONLY. The site is prerendered; whether to show this depends
//     on localStorage, which the prerender cannot know. Rendering null on the
//     first client render — the same thing the server wrote — and deciding in
//     an effect is what keeps hydration clean.
//  3. NO GEO-DETECTION. Everyone is asked. Deciding who "needs" a banner by
//     inferring a country means reading the IP address, which is the one thing
//     this site's analytics contract promises never to record.
//  4. ESCAPE DISMISSES, IT DOES NOT ANSWER. Pressing Escape puts the bar away
//     for this page load without recording a choice, so the next visit asks
//     again. Silence is not consent, and it is not refusal either — it is the
//     default tier, which is the cookieless one.
//  5. REOPENABLE. `openConsentBar()` (below) shows it again, whatever was
//     chosen before, so the footer's "Cookie choices" link is a real control
//     and not a link to a paragraph about one.

/** The exact sentence, agreed with the owner. Do not paraphrase it in a lane. */
const CONSENT_COPY =
  "Capnolog uses cookies and session replays to see how this site is used. " +
  "Nothing here is medical data.";

/** The phone's one row: the sentence's first half, word for word. */
const CONSENT_PHONE_COPY = CONSENT_COPY.slice(
  0,
  CONSENT_COPY.indexOf(". ") + 1,
);

/** Decline and Accept, one style for both. */
const CONSENT_BUTTON =
  "min-h-9 px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-lg border border-border bg-background text-xs sm:text-sm font-semibold text-foreground hover:bg-muted transition-colors";

// A two-line event bus. The footer link lives in another component tree (and is
// written by a different lane), so it needs a way to open this without a shared
// provider, a context, or a global store the site does not otherwise have.
const openListeners = new Set<() => void>();

/**
 * Show the cookie bar again, whatever the visitor chose before.
 *
 * THIS IS THE EXPORT THE FOOTER'S "Cookie choices" LINK CALLS:
 *
 * ```tsx
 * import { openConsentBar } from "@/components/layout/ConsentBar";
 * <button type="button" onClick={openConsentBar}>Cookie choices</button>
 * ```
 *
 * Safe to call at any time, including before the bar has mounted (nothing
 * happens) and on a page where DNT/GPC has switched analytics off entirely.
 */
export function openConsentBar(): void {
  for (const listener of openListeners) listener();
}

export function ConsentBar() {
  // Never true on the server or on the first client render — see note 2.
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (shouldAskForConsent()) setVisible(true);
    const stopOpen = (() => {
      const listener = () => setVisible(true);
      openListeners.add(listener);
      return () => openListeners.delete(listener);
    })();
    // Another tab, or a future in-app control, answering for this one.
    const stopConsent = onConsentChange(() => setVisible(false));
    return () => {
      stopOpen();
      stopConsent();
    };
  }, []);

  // Counted once per time it appears, so `consent_choice / consent_shown` is a
  // real rate rather than a number floating on its own.
  useEffect(() => {
    if (visible) track("consent_shown");
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setVisible(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [visible]);

  if (!visible) return null;

  const choose = (choice: "granted" | "denied") => {
    setConsent(choice);
    setVisible(false);
  };

  return (
    <div
      role="region"
      aria-label="Cookie choices"
      className="fixed inset-x-0 bottom-0 z-50 p-2 sm:p-4 pointer-events-none"
    >
      <div
        // `.well` is the tinted plate recipe (styles/premium.css); the lift is
        // the standard card token, because this genuinely floats over the page.
        //
        // ONE ROW ON A PHONE (owner, 2026-10-11). The two-row bar still sat
        // over the hero's picture on the first screen, and 96% of ad visits
        // never scrolled past that screen. So a phone gets the sentence's first
        // half as the link to the full explanation, beside the two buttons;
        // a wider screen keeps the whole sentence and the named link. The
        // copy is the owner's, cut, not paraphrased.
        //
        // EQUAL BUTTONS (owner, same day): Decline and Accept share one style,
        // so neither answer is the one the page leans toward. The rate to
        // watch is `consent_choice / consent_shown`.
        className="well pointer-events-auto mx-auto max-w-3xl px-3 py-2 sm:px-5 sm:py-4 flex flex-row items-center gap-3 sm:gap-4"
        style={{ boxShadow: "var(--shadow-2)" }}
      >
        <p className="min-w-0 flex-1 text-[11px] sm:text-sm text-muted-foreground leading-[1.35] sm:leading-relaxed">
          {/* A plain anchor, not a wouter <Link>: this is a jump to a heading
              on another prerendered page, and only a real navigation lands on
              the anchor. It is same-origin, so it fires no outbound_click.

              The text says where it goes. "Learn more" was the site's one
              failing Lighthouse link-text audit, and it is the same problem for
              a screen-reader user pulling up a list of links: a destination
              named only by the sentence it happens to sit after. On a phone
              the sentence is the link, and the sentence says what the page
              behind it explains. */}
          <a
            href="/privacy#website-analytics"
            className="underline underline-offset-2 sm:hidden"
          >
            {CONSENT_PHONE_COPY}
          </a>
          <span className="hidden sm:inline">
            {CONSENT_COPY}{" "}
            <a
              href="/privacy#website-analytics"
              className="plate-accent font-medium underline underline-offset-2"
            >
              How we measure this site
            </a>
          </span>
        </p>
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <button
            type="button"
            onClick={() => choose("denied")}
            className={CONSENT_BUTTON}
          >
            Decline
          </button>
          <button
            type="button"
            onClick={() => choose("granted")}
            className={CONSENT_BUTTON}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConsentBar;
